import React, { useState } from "react";
import {
  UserPlusIcon,
  UserMinusIcon,
  Share2,
  Loader2,
  Pencil,
} from "lucide-react";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import logger from "@/utils/logger";

interface AccountHeaderProps {
  profileImage: string;
  backgroundImage: string;
  name: string;
  username: string;
  isPrivate: boolean;
  isCurrentUser: boolean;
  isFollowing: boolean;
  isRequested: boolean;
  followerCount: number;
  followingCount: number;
  isFollowLoading: boolean;
  isBackgroundLoading: boolean;
  onEditProfile: () => void;
  onFollowToggle: () => void;
  onProfileImageClick: () => void;
  onOpenBackgroundModal: () => void;
  onConnectionsClick: (tab: "followers" | "following") => void;
}

const AccountHeader: React.FC<AccountHeaderProps> = ({
  profileImage,
  backgroundImage,
  name,
  username,
  isPrivate,
  isCurrentUser,
  isFollowing,
  isRequested,
  followerCount,
  followingCount,
  isFollowLoading,
  isBackgroundLoading,
  onEditProfile,
  onFollowToggle,
  onProfileImageClick,
  onOpenBackgroundModal,
  onConnectionsClick,
}) => {
  return (
    <>
      {/* Header Background */}
      <div className="w-full h-[200px] relative">
        <LazyLoad
          src={backgroundImage}
          lowResSrc={backgroundImage}
          alt="Profile background"
          className="w-full h-full object-cover rounded-xl"
        />
        {isBackgroundLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm transition-all duration-300 rounded-xl">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
          </div>
        )}
        {isCurrentUser && (
          <button
            onClick={onOpenBackgroundModal}
            className="absolute top-2 right-2 px-3 py-2 rounded-lg bg-white shadow-md flex items-center justify-center space-x-2 hover:bg-neutral-50 opacity-75 hover:opacity-100 transition-all"
          >
            <span className="text-neutral-600 text-xs 2xl:text-[14px] font-light">
              Change Background
            </span>
            <Pencil
              className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-neutral-600"
              strokeWidth={1.5}
            />
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="w-full transform -translate-y-12 -mb-6">
        {/* Profile Header */}
        <div className="flex items-center justify-between w-full">
          {/* Left side - Profile Image and Basic Info */}
          <div className="flex flex-col h-full w-full">
            <div className="flex items-start space-x-4 pl-4">
              <div className="relative">
                <AvatarImage
                  src={profileImage}
                  className="w-36 h-36 border-8 border-white"
                  firstName={name?.split(" ")[0]}
                  lastName={name?.split(" ")[1]}
                />

                {isCurrentUser && (
                  <button
                    onClick={onProfileImageClick}
                    className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-neutral-50 transition-colors"
                  >
                    <i className="fa-solid fa-camera text-neutral-600 text-sm" />
                  </button>
                )}
              </div>
              <div className="flex flex-col space-y-2 ml-8">
                <div className="pt-16 flex flex-row space-x-2.5 items-center">
                  <h1 className="text-2xl font-semibold">{name}</h1>
                  <div className="w-2 h-2 bg-indigo-800 rounded-full"></div>
                  <div className="flex items-center gap-2">
                    <p className="text-neutral-600 text-sm 2xl:text-[15px] font-light">
                      @{username}
                    </p>
                    {isPrivate && (
                      <span className="inline-flex items-center rounded-md bg-neutral-50 px-2 py-1 text-xs font-medium text-neutral-700 ring-1 ring-inset ring-neutral-700/10">
                        Private Account
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  {isCurrentUser ? (
                    <div className="flex gap-2">
                      <button
                        onClick={onEditProfile}
                        className="inline-flex w-28 items-center justify-center gap-2.5 py-2 text-xs 2xl:text-sm 2xl:w-32 font-normal rounded-lg shadow-sm disabled:opacity-50 transition-all duration-200 bg-white text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 border border-neutral-200"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                        Edit Profile
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={onFollowToggle}
                        disabled={isFollowLoading}
                        className={`inline-flex w-28 items-center justify-center gap-2.5 py-2 text-xs font-normal rounded-lg shadow-sm disabled:opacity-50 transition-all duration-200 ${
                          isFollowing || isRequested
                            ? "bg-white text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 border border-neutral-200"
                            : "bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800"
                        }`}
                      >
                        {isFollowLoading ? (
                          <>
                            <Loader2
                              className="w-4 h-4 animate-spin"
                              strokeWidth={1.5}
                            />
                            Loading...
                          </>
                        ) : isRequested ? (
                          <>
                            <UserPlusIcon
                              className="w-4 h-4"
                              strokeWidth={1.5}
                            />
                            Requested
                          </>
                        ) : isFollowing ? (
                          <>
                            <UserMinusIcon
                              className="w-4 h-4"
                              strokeWidth={1.5}
                            />
                            Following
                          </>
                        ) : (
                          <>
                            <UserPlusIcon
                              className="w-4 h-4"
                              strokeWidth={1.5}
                            />
                            Follow
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          navigator
                            .share({
                              title: `${name}'s Profile`,
                              text: `Check out ${name}'s profile`,
                              url: window.location.href,
                            })
                            .catch((err) => logger.log("Share failed:", err));
                        }}
                        className="inline-flex w-28 items-center justify-center gap-2.5 py-2 text-xs font-normal rounded-lg shadow-sm bg-white text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 border border-neutral-200 transition-all duration-200"
                      >
                        <Share2 className="w-4 h-4" strokeWidth={1.5} />
                        Share
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Stats */}
          <div className="flex flex-row space-x-4 text-center transform translate-y-4">
            <div
              className="px-4 py-2 rounded-xl transition-all duration-200 flex flex-row space-x-3 items-end cursor-pointer hover:bg-neutral-50 hover:shadow-sm group"
              onClick={() => onConnectionsClick("followers")}
            >
              <div className="text-4xl font-semibold text-slate-800 group-hover:text-indigo-700 transition-colors">
                {followerCount}
              </div>
              <div className="text-md text-neutral-600 -translate-y-0.5 group-hover:text-indigo-600 transition-colors">
                Followers
              </div>
            </div>
            <div
              className="px-4 py-2 rounded-xl transition-all duration-200 flex flex-row space-x-3 items-end cursor-pointer hover:bg-neutral-50 hover:shadow-sm group"
              onClick={() => onConnectionsClick("following")}
            >
              <div className="text-4xl font-semibold text-slate-800 group-hover:text-indigo-700 transition-colors">
                {followingCount}
              </div>
              <div className="text-md text-neutral-600 -translate-y-0.5 group-hover:text-indigo-600 transition-colors">
                Following
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountHeader;
